import { ng } from "@common/ngds";
import { Drawer, PathBuilder, Point, StyleForLine, Svg } from "@common/components/Rendering";
import { useSelector } from "react-redux";
import {
  ArrowId,
  ColumnMarkers,
  cs_force_text,
  diagram_header_style,
  isometric_3d,
  label_style,
  long_reinf_style,
} from "../ViewportStyles";
import { useProject } from "../../features/projects/projectSlice";
import {
  selectAxisLabelOffset3d,
  selectAxisOffset3d,
  selectAxisSize3d,
  selectCardboardDistance3d,
  selectResultBoardGraphAmplitude3d,
  selectResultBoardClearance3d,
  selectResultBoardHeaderOffsetsLeft3d,
  selectResultBoardHeaderOffsetsRight3d,
  ResultBoardHeaderOffsets3d,
  selectOffsetLoadsFromColumn3d,
  selectResultBoardLaneWidth3d,
  selectRightHand3d,
  selectStartCardboardsAtOuterCenterLine3d,
  selectShow3dCardboard,
  selectShow3dLoadsOnOppositeSide,
  selectShow3dReinforcement,
  selectShowSectionBuckling,
  selectShowSectionDimensions,
  selectScale3dX,
  selectScale3dYZ,
  selectLoadColumnSpacing3d,
  selectGeometryDebug3d,
  selectDebugExtrusion3d,
  selectDebugExtrusionNormals3d,
  selectDebugExtrusionNormalsCenter3d,
  selectDebugExtrusionNormalsRadius3d,
  selectDrawFaces3d,
  selectShadeFaces3d,
  selectLightYaw3d,
  selectLightPitch3d,
  selectShowLightDirection3d,
  selectShadeLighterTimberPercent3d,
  selectShadeLighterPercent3d,
  selectShadeDarkerPercent3d,
  selectHiddenLines3d,
  selectHiddenLineGrey3d,
  HiddenLines3d,
} from "../../features/ui/uiSlice";
import { useDevConfig } from "../../App";
import { useAutoZoomViewport } from "../../features/user/userSlice";
import {
  calculateGeomCrossSection,
  Extent,
  getGeomCrossSectionPoints,
  shouldUseFakeSilhouetteForSection,
} from "./drawGeomCrossSection";
import {
  FACE_SIDE_COLOR,
  dot,
  getExtrusionCapNormal,
  getExtrusionFaceNormals,
  ISOMETRIC_VIEW_DIRECTION,
  ngLightDirection,
  selectExtrusionEdges,
  selectExtrusionFaceNormalsForDebug,
  shadeFactor,
  shadeFaceColor,
  SVG_ISOMETRIC_VIEW_DIRECTION,
  svgFromNgDirection,
  Vec3,
} from "./extrusionEdges";
import { classifyMeshEdges, scalePlacedMesh, transformMesh, visibleMeshFaces } from "./mesh";
import { collectSupportMeshInstances, MeshInstance } from "./supportMeshes";
import { calculate_column_side_geom_size } from "./drawSideGeometry";
import { columnLighterPercent, columnMaterialFill, columnShadedFill, columnTopFill } from "./shared";
import { GraphDirection } from "./SideView";
import {
  CreateGraphValues,
  DiagramResults,
  DiagramValueRanges,
  isometricDiagramValueRanges,
  layoutDiagramHeader,
} from "./drawSectionForceDiagrams";
import type { GraphPointsMode } from "../Viewport";
import { create_buckling_data } from "./drawBucklingData";
import { getStandaloneBars, REBAR_DIAMETER_MM_TO_RADIUS_M } from "./drawLongitudalReinf";
import { classify_direction, force_from_point_load, get_loads, getSelectedLoadcaseGuids } from "./drawLoads";
import { loadcases_getColor } from "../../helpers/Colors";
import { HiddenLineScene } from "./hiddenLineRemoval";
import { isoProject, IsoScale, Point3D } from "./isometric";
import {
  RESULT_BOARD_LANE_COUNT,
  ResultBoardDiagramType,
  resultBoardLaneIndex,
  resultBoardLoadBase,
  loadCaseLanes,
  eccentricityOffsetPath,
  ResultBoardScreenSide,
  resultBoardScreenSide,
  resultBoardValueLabels,
  resultBoardWidth,
} from "./resultBoard";

interface View3DProps {
  results: DiagramResults;
  selectedLcombGuid: string;
  graphPointsMode: GraphPointsMode;
}

interface IsometricDrawingContext {
  drawer: Drawer<ArrowId>;
  projectData: ng.frontend.column.Project | null;
  scale: IsoScale;
  scaleYZ: number;
  columnHeight: number;
  halfY: number;
  halfZ: number;
  outlineColor: string;
  fakeSilhouette: boolean;
  debugExtrusion: boolean;
  debugExtrusionNormals: boolean;
  debugExtrusionNormalsCenter: number;
  debugExtrusionNormalsRadius: number;
  drawFaces: boolean;
  shadeFaces: boolean;
  /** Shade-faces light, in ng space (see ngLightDirection). */
  lightDirection: Vec3;
  /** Style for outline edges whose adjacent faces all face away from the viewer; null leaves them out. */
  hiddenLineStyle: StyleForLine | null;
  columnSideColor: string;
  columnTopColor: string;
  /** Column color at a shading lightness from 0 (unlit) to 1 (lit). */
  columnShadedColor: (lightness: number) => string;
  /** Back-to-front paint queue for faces, flushed between supports and column sides. */
  painter: PainterQueue;
  /** Collects every outline edge and the front faces that can hide them. */
  hiddenLines: HiddenLineScene;
  point: (x: number, y: number, z: number) => Point3D;
  projectPoint: (point: Point3D) => Point;
  rightHand: boolean;
}

type ColumnCrossSection = ReturnType<typeof getGeomCrossSectionPoints>;

interface ResultBoardSettings {
  results: DiagramResults;
  selectedLcombGuid: string;
  graphPointsMode: GraphPointsMode;
  cardboardDistance: number;
  laneWidth: number;
  graphAmplitude: number;
  overlapClearance: number;
  /** x offset (m) per lane header (N, M/V, d), for the board on the left / right of the screen */
  headerOffsets: Record<ResultBoardScreenSide, ResultBoardHeaderOffsets3d>;
  showCardboard: boolean;
  startAtOuterCenterLine: boolean;
  offsetLoadsFromColumn: boolean;
  loadColumnSpacing: number;
  // One value range per diagram type, held fixed across every load combination, both view
  // directions and the 1st/2nd order toggle - see isometricDiagramValueRanges. Without it each
  // curve is normalized to its own max and always fills the full board amplitude, so switching
  // combination, direction or order never changes the drawn size.
  sharedValueRanges: DiagramValueRanges;
}

interface LoadDrawingSettings {
  selectedLcombGuid: string;
  cardboardDistance: number;
  showDimensions: boolean;
  startAtOuterCenterLine: boolean;
  showOnOppositeSide: boolean;
  showCardboard: boolean;
  offsetLoadsFromColumn: boolean;
  loadColumnSpacing: number;
}

interface AxisSettings {
  size: number;
  offset: number;
  labelOffset: number;
  /** Direction the shade-faces light travels, in ng space, drawn as an arrow from the origin; null hides it. */
  lightRay: Vec3 | null;
}

const graphDirections: GraphDirection[] = ["xy", "xz"];
const graphTypes: ResultBoardDiagramType[] = ["M", "V", "N", "D"];
// Overlap separately antialiased SVG faces so the background cannot show at shared edges.
const FACE_SEAM_STROKE_PX = 0.1;

const hiddenLineStyleFor = (
  hiddenLines: HiddenLines3d,
  outlineColor: string,
  greyPercent: number,
): StyleForLine | null => {
  const grey = isometric_3d.colors.hiddenLine(greyPercent);
  switch (hiddenLines) {
    case "hide":
      return null;
    case "dashed":
      return { lineColor: outlineColor, thickness: 1, dashed: true };
    case "grey":
      return { lineColor: grey, thickness: 1 };
    case "dashedGrey":
      return { lineColor: grey, thickness: 1, dashed: true };
    case "solid":
      return { lineColor: outlineColor, thickness: 1 };
  }
};

interface PendingFace {
  depth: number;
  draw: () => void;
}

// Faces are painted back-to-front, so a face nearer the camera covers what is behind it. Edges are
// not queued; they are split into visible and hidden pieces by HiddenLineScene and drawn last.
class PainterQueue {
  private faces: PendingFace[] = [];

  addFace(depth: number, draw: () => void): void {
    this.faces.push({ depth, draw });
  }

  /** Paints everything queued so far, furthest-from-camera first, and empties the queue. */
  flush(): void {
    const faces = [...this.faces];
    this.faces = [];
    faces.sort((a, b) => a.depth - b.depth).forEach((face) => face.draw());
  }
}
const ISOMETRIC_HORIZONTAL_SCALE = Math.cos(Math.PI / 6);

// Distance from the column axis to the near edge of a load board. With offsetFromColumn, the board
// is pushed out until it clears the column's silhouette by columnSpacing on screen: the board lies
// in the plane of the other transverse axis, and the silhouette's outer corner is at halfY + halfZ.
const loadBoardNear = (
  currDir: GraphDirection,
  halfY: number,
  halfZ: number,
  scaleYZ: number,
  cardboardDistance: number,
  startAtOuterCenterLine: boolean,
  offsetFromColumn: boolean,
  columnSpacing: number,
) => {
  if (!offsetFromColumn) return resultBoardLoadBase(currDir === "xy" ? halfY : halfZ, cardboardDistance);
  const boardPlane = startAtOuterCenterLine ? 0 : -(currDir === "xy" ? halfZ : halfY);
  return halfY + halfZ - boardPlane + columnSpacing / (ISOMETRIC_HORIZONTAL_SCALE * scaleYZ);
};

const loadPanelOuterOffset = (
  projectData: ng.frontend.column.Project | null,
  selectedLcombGuid: string,
  currDir: GraphDirection,
  cardboardDistance: number,
  halfY: number,
  halfZ: number,
  scaleYZ: number,
  startAtOuterCenterLine: boolean,
  offsetLoadsFromColumn: boolean,
  loadColumnSpacing: number,
): number => {
  const base = loadBoardNear(
    currDir,
    halfY,
    halfZ,
    scaleYZ,
    cardboardDistance,
    startAtOuterCenterLine,
    offsetLoadsFromColumn,
    loadColumnSpacing,
  );
  if (!projectData) return base;

  const { horizontal, line } = get_loads(projectData);
  const selectedLoadcaseGuids = getSelectedLoadcaseGuids(projectData, selectedLcombGuid);
  const isTransverseLoad = (load: ng.load.Data) => {
    if (!load.direction || !load.lcaseGuid || !selectedLoadcaseGuids.has(load.lcaseGuid)) return false;
    const direction = classify_direction(load.direction);
    return (direction === "y" ? "xy" : direction === "z" ? "xz" : null) === currDir;
  };
  const loads = [...horizontal.filter(isTransverseLoad), ...line.filter(isTransverseLoad)];
  const magnitudes = [...horizontal, ...line]
    .filter((load) => {
      if (!load.direction || !load.lcaseGuid || !selectedLoadcaseGuids.has(load.lcaseGuid)) return false;
      return classify_direction(load.direction) !== "x";
    })
    .flatMap((load) => (line.includes(load) ? (load.values ?? []) : [force_from_point_load(load)]))
    .map(Math.abs)
    .filter(Number.isFinite);
  const horizontalScale =
    magnitudes.length > 0
      ? (isometric_3d.load.horizontalAmplitude - isometric_3d.load.minimumAmplitude) /
      Math.max(...magnitudes, 1e-9)
      : 0;
  const lanes = loadCaseLanes(
    loads,
    (load) =>
      isometric_3d.load.minimumAmplitude +
      (line.includes(load)
        ? Math.max(...(load.values ?? []).map(Math.abs), 0)
        : Math.abs(force_from_point_load(load))) * horizontalScale,
    isometric_3d.load.laneSpacing,
  );
  return base + lanes.outer;
};

// section half-extent in y (for xy-side graphs) and z (for xz-side graphs), in meters
const halfExtentFor = (
  projectData: ng.frontend.column.Project | null,
  geomExtent: Extent | null,
  currDir: GraphDirection,
): number => {
  const { size } = calculate_column_side_geom_size(projectData, currDir);
  if (Number.isFinite(size) && size > 0) return size / 2;
  if (geomExtent) {
    return currDir === "xy"
      ? (geomExtent.hor.max - geomExtent.hor.min) / 2
      : (geomExtent.ver.max - geomExtent.ver.min) / 2;
  }
  return 0.15;
};

const queueColumnSides = ({
  drawer,
  projectData,
  scale,
  columnHeight,
  halfY,
  halfZ,
  fakeSilhouette,
  debugExtrusion,
  debugExtrusionNormals,
  debugExtrusionNormalsCenter,
  debugExtrusionNormalsRadius,
  drawFaces,
  shadeFaces,
  lightDirection,
  columnSideColor,
  columnShadedColor,
  painter,
  hiddenLines,
  point,
  projectPoint,
}: IsometricDrawingContext): ColumnCrossSection => {
  const bottom = -0;
  const top = -columnHeight;

  // point() returns raw (unscaled) coordinates - scale3dX/scale3dYZ are applied later, inside
  // projectPoint/isoProject, at projection time. selectExtrusionEdges, however, needs points in
  // the same (potentially non-uniformly scaled) space the fixed isometric camera actually views -
  // see ISOMETRIC_VIEW_DIRECTION's docs - so silhouette/crease detection is run on separately
  // scaled copies of the points, without altering what's actually drawn/projected.
  const scaledForEdgeSelection = (p: Point3D): Point3D => ({
    x: p.x * scale.x,
    y: p.y * scale.yz,
    z: p.z * scale.yz,
  });

  // Extrudes a cross-section outline loop (perimeter or hole) along the column height: computes
  // the bottom/top point loops, decides which vertical edges to keep, queues its front-facing
  // side faces and registers them and its outlines for hidden-line removal.
  // Outlines with many points (e.g. a circle approximated by a polygon) only get a vertical edge
  // where it is a silhouette or a sufficiently sharp corner - see selectExtrusionEdges - to avoid
  // a cluttered "comb" of parallel lines.
  const prepareLoop = (points2d: Point[]) => {
    if (points2d.length < 2) return;
    const bottomPoints = points2d.map(([y, z]) => point(bottom, y, z));
    const topPoints = points2d.map(([y, z]) => point(top, y, z));
    const keepVertical = selectExtrusionEdges(
      bottomPoints.map(scaledForEdgeSelection),
      topPoints.map(scaledForEdgeSelection),
      SVG_ISOMETRIC_VIEW_DIRECTION,
      fakeSilhouette,
    );

    if (debugExtrusionNormals) {
      const scaledBottomPoints = bottomPoints.map(scaledForEdgeSelection);
      const scaledTopPoints = topPoints.map(scaledForEdgeSelection);
      const normalLength = Math.max(0.05, Math.min(0.25, Math.max(halfY, halfZ) * 0.35)) * scale.yz;
      const fromScaledSpace = (p: Point3D): Point3D => ({
        x: p.x / scale.x,
        y: p.y / scale.yz,
        z: p.z / scale.yz,
      });

      const debugFaces = selectExtrusionFaceNormalsForDebug(
        getExtrusionFaceNormals(scaledBottomPoints, scaledTopPoints, SVG_ISOMETRIC_VIEW_DIRECTION),
        debugExtrusionNormalsCenter,
        debugExtrusionNormalsRadius,
      );
      for (const face of debugFaces) {
        const color = face.isEdgeOn
          ? isometric_3d.colors.debugNormalEdgeOn
          : face.viewDot > 0
            ? isometric_3d.colors.debugNormalFront
            : isometric_3d.colors.debugNormalBack;
        const end = {
          x: face.center.x + face.normal.x * normalLength,
          y: face.center.y + face.normal.y * normalLength,
          z: face.center.z + face.normal.z * normalLength,
        };
        const from = fromScaledSpace(face.center);
        const to = fromScaledSpace(end);
        drawer.drawLine(
          projectPoint(from),
          projectPoint(to),
          { lineColor: color, thickness: 1.5 },
          { endArrow: "load-arrow", title: `face ${face.fromIndex}->${face.toIndex}: ${face.viewDot}` },
        );
        drawer.drawText(
          projectPoint(to),
          `${face.fromIndex}->${face.toIndex}: ${face.viewDot.toFixed(3)}`,
          { ...label_style, lineColor: color, textSize: 9 },
          { horiAlign: "left", vertAlign: "middle", horiOffset: 3 },
        );
      }
    }

    const allLoopFaces = getExtrusionFaceNormals(
      bottomPoints.map(scaledForEdgeSelection),
      topPoints.map(scaledForEdgeSelection),
      SVG_ISOMETRIC_VIEW_DIRECTION,
    );

    // Only faces facing the (fixed isometric) view direction are painted and hide lines - the back
    // side of the extrusion is never visible from this camera.
    for (const face of allLoopFaces) {
      if (face.viewDot <= 0) continue;
      const i = face.fromIndex;
      const next = face.toIndex;
      const quadPoints = [bottomPoints[i], bottomPoints[next], topPoints[next], topPoints[i]];
      hiddenLines.addOccluder([quadPoints]);
      if (!drawFaces) continue;
      const color = shadeFaces
        ? columnShadedColor(shadeFactor(face.normal, svgFromNgDirection(lightDirection)))
        : columnSideColor;
      painter.addFace(dot(face.center, SVG_ISOMETRIC_VIEW_DIRECTION), () => {
        const quad = new PathBuilder().moveTo(projectPoint(quadPoints[0]));
        quadPoints.slice(1).forEach((p) => quad.drawLineTo(projectPoint(p)));
        quad.closePath();
        drawer.drawPath({ lineColor: color, thickness: FACE_SEAM_STROKE_PX, fill: color }, quad);
      });
    }

    for (let i = 0; i < points2d.length; i++) {
      const next = (i + 1) % points2d.length;
      hiddenLines.addEdge(bottomPoints[i], bottomPoints[next]);
      hiddenLines.addEdge(topPoints[i], topPoints[next]);
      if (keepVertical[i].keep) {
        hiddenLines.addEdge(bottomPoints[i], topPoints[i], debugExtrusion ? keepVertical[i].debugInfo : undefined);
      }
    }
  };

  const crossSection = projectData ? getGeomCrossSectionPoints(projectData) : null;
  if (crossSection) {
    prepareLoop(crossSection.perimeter);
    for (const hole of crossSection.holes) {
      prepareLoop(hole);
    }
    const toTop = (loop: Point[]) => loop.map(([y, z]) => point(top, y, z));
    hiddenLines.addOccluder([toTop(crossSection.perimeter), ...crossSection.holes.map(toTop)]);
    return crossSection;
  }

  // fallback: no section geometry available - draw a simple box outline sized from the extent so
  // the view is not left empty.
  const corners = (x: number) => [
    point(x, -halfY, -halfZ),
    point(x, halfY, -halfZ),
    point(x, halfY, halfZ),
    point(x, -halfY, halfZ),
  ];
  const bottomCorners = corners(bottom);
  const topCorners = corners(top);
  for (let i = 0; i < 4; i++) {
    const next = (i + 1) % 4;
    hiddenLines.addEdge(bottomCorners[i], bottomCorners[next]);
    hiddenLines.addEdge(topCorners[i], topCorners[next]);
    hiddenLines.addEdge(bottomCorners[i], topCorners[i]);
  }
  return null;
};

const drawColumnTopFace = (
  {
    drawer,
    columnHeight,
    drawFaces,
    shadeFaces,
    lightDirection,
    columnTopColor,
    columnShadedColor,
    point,
    projectPoint,
  }: IsometricDrawingContext,
  crossSection: ColumnCrossSection,
) => {
  if (!drawFaces || !crossSection || crossSection.perimeter.length === 0) return;
  const top = -columnHeight;
  const [firstY, firstZ] = crossSection.perimeter[0];
  const capNormal = getExtrusionCapNormal([point(0, firstY, firstZ)], [point(top, firstY, firstZ)]);
  const capPath = new PathBuilder();
  const drawLoop = (loopPoints: Point[]) => {
    let first = true;
    for (const [y, z] of loopPoints) {
      const projected = projectPoint(point(top, y, z));
      if (first) capPath.moveTo(projected);
      else capPath.drawLineTo(projected);
      first = false;
    }
  };
  drawLoop(crossSection.perimeter);
  for (const hole of crossSection.holes) {
    if (hole.length === 0) continue;
    drawLoop(hole);
  }

  const capColor = shadeFaces
    ? columnShadedColor(shadeFactor(capNormal, svgFromNgDirection(lightDirection)))
    : columnTopColor;
  drawer.drawPath({ lineColor: "transparent", thickness: 0, fill: capColor }, capPath);
};

// Hidden pieces first, so a visible line drawn over the same pixels wins.
const drawEdges = ({ drawer, hiddenLines, outlineColor, hiddenLineStyle }: IsometricDrawingContext) => {
  const pieces = hiddenLines.resolve();
  const visibleStyle = { lineColor: outlineColor, thickness: 1 };
  const draw = (hidden: boolean, style: StyleForLine) => {
    for (const piece of pieces) {
      if (piece.hidden !== hidden) continue;
      drawer.drawLine(piece.from, piece.to, style, piece.title ? { title: piece.title } : undefined);
    }
  };
  if (hiddenLineStyle) draw(true, hiddenLineStyle);
  draw(false, visibleStyle);
};

const drawLongitudinalReinforcement = ({
  drawer,
  projectData,
  scaleYZ,
  point,
  projectPoint,
}: IsometricDrawingContext) => {
  const standaloneBars = getStandaloneBars(projectData?.data ?? null);
  for (const bar of standaloneBars) {
    const start = bar.startPoint;
    const end = bar.endPoint;
    if (!start || !end) continue;

    const radius = bar.diameter * REBAR_DIAMETER_MM_TO_RADIUS_M * scaleYZ;
    const startPoint = projectPoint(point(-start.x, start.y, start.z));
    const endPoint = projectPoint(point(-end.x, end.y, end.z));
    drawer.drawLine(startPoint, endPoint, { lineColor: long_reinf_style.lineColor, thickness: 1 });
    drawer.drawCircle(startPoint, radius, long_reinf_style);
    drawer.drawCircle(endPoint, radius, long_reinf_style);
  }
};

const diagramColor = (type: ResultBoardDiagramType): string =>
  type === "M"
    ? isometric_3d.colors.momentLine
    : type === "V"
      ? isometric_3d.colors.shearLine
      : type === "N"
        ? isometric_3d.colors.normalLine
        : isometric_3d.colors.deformationLine;

const diagramFillColor = (type: ResultBoardDiagramType): string | null => {
  if (type === "D") return null;
  const fill =
    type === "M"
      ? isometric_3d.colors.momentFill
      : type === "V"
        ? isometric_3d.colors.shearFill
        : isometric_3d.colors.normalFill;
  return type === "M" || type === "V"
    ? `color-mix(in srgb, ${fill} ${isometric_3d.diagram.fillOpacity * 100}%, transparent)`
    : fill;
};

// CSS color-mix fade, same technique as the 2D side view's shared-lane overlay (DrawGraph in
// drawSectionForceDiagrams.ts) - works here too since the isometric drawer paints with ordinary
// SVG stroke/fill color strings (including the viewport's CSS custom properties).
const fadedCssColor = (color: string, visiblePercent: number): string =>
  `color-mix(in srgb, ${color} ${visiblePercent}%, transparent)`;

const drawDiagramCurve = (
  context: IsometricDrawingContext,
  boardPoint: (x: number, transverse: number) => Point3D,
  zeroOffset: number,
  graphScale: number,
  values: ReturnType<typeof CreateGraphValues>,
  lineColor: string,
  fillColor: string | null,
) => {
  const { drawer, projectPoint } = context;

  if (fillColor) {
    for (let index = 0; index + 1 < values.length; index++) {
      const current = values[index];
      const next = values[index + 1];
      const currentX = -current.horizontalValue;
      const nextX = -next.horizontalValue;
      const currentGraphOffset = zeroOffset - current.verticalValue * graphScale;
      const nextGraphOffset = zeroOffset - next.verticalValue * graphScale;
      const fillPath = new PathBuilder()
        .moveTo(projectPoint(boardPoint(currentX, zeroOffset)))
        .drawLineTo(projectPoint(boardPoint(currentX, currentGraphOffset)))
        .drawLineTo(projectPoint(boardPoint(nextX, nextGraphOffset)))
        .drawLineTo(projectPoint(boardPoint(nextX, zeroOffset)))
        .closePath();
      drawer.drawPath({ lineColor: "transparent", thickness: 0, fill: fillColor }, fillPath);
    }
  }

  const path = new PathBuilder();
  values.forEach((value, index) => {
    const x = -value.horizontalValue;
    const offset = zeroOffset - value.verticalValue * graphScale;
    const projectedPoint = projectPoint(boardPoint(x, offset));
    if (index === 0) path.moveTo(projectedPoint);
    else path.drawLineTo(projectedPoint);
  });

  drawer.drawPath({ lineColor, thickness: 1, fill: null }, path);
};

const drawDiagram = (
  context: IsometricDrawingContext,
  settings: ResultBoardSettings,
  currDir: GraphDirection,
  boardNear: number,
  boardPoint: (x: number, transverse: number) => Point3D,
  diagramType: ResultBoardDiagramType,
  values: ReturnType<typeof CreateGraphValues>,
  // The 1st order counterpart of `values`, only passed when both the 1st and 2nd order results
  // are shown at once (App's showBothOrders) - see DrawGraph's matching parameter for why.
  secondaryValues?: ReturnType<typeof CreateGraphValues>,
) => {
  const { drawer, projectPoint } = context;
  const { laneWidth, graphAmplitude, graphPointsMode, sharedValueRanges } = settings;
  // Shares one scale with the secondary curve (like the 2D side view) so the faded reference
  // curve stays inside its own lane rather than being clamped against a narrower primary range.
  const combinedAbsValues = [...values, ...(secondaryValues ?? [])].map((value) => Math.abs(value.verticalValue));
  const localMaxAbs = Math.max(...combinedAbsValues, 1e-9);
  // Fixed across every load combination, view direction and the 1st/2nd order toggle (see
  // isometricDiagramValueRanges), so the drawn size actually reflects the magnitude instead of
  // every curve independently filling the lane. The local max is still a floor in case the
  // shared range is somehow stale (e.g. computed from the local max if sharing it would clamp it).
  const sharedRange = sharedValueRanges[diagramType];
  const valueRange = sharedRange !== undefined && sharedRange >= localMaxAbs ? sharedRange : localMaxAbs;
  const effectiveValueRange = valueRange > 0 ? valueRange : 1;
  const graphScale = graphAmplitude / effectiveValueRange;
  const zeroOffset = boardNear + laneWidth * (resultBoardLaneIndex(diagramType) + 0.5);
  const fillColor = diagramFillColor(diagramType);
  const lineColor = diagramColor(diagramType);

  drawDiagramCurve(context, boardPoint, zeroOffset, graphScale, values, lineColor, fillColor);

  // Overlay: the faded 1st order reference curve, same lane and scale as the curve just drawn.
  // No labels, no graph points - a visual comparison only.
  if (secondaryValues && secondaryValues.length > 0) {
    drawDiagramCurve(
      context,
      boardPoint,
      zeroOffset,
      graphScale,
      secondaryValues,
      fadedCssColor(lineColor, 40),
      fillColor ? fadedCssColor(fillColor, 25) : null,
    );
  }

  for (const label of resultBoardValueLabels(values, diagramType, graphPointsMode)) {
    const graphOffset = zeroOffset - label.verticalValue * graphScale;
    const labelOffset =
      graphOffset + (label.verticalValue < 0 ? isometric_3d.diagram.labelOffset : -isometric_3d.diagram.labelOffset);
    const xOffset =
      diagramType === "M"
        ? -isometric_3d.diagram.momentLabelXOffset
        : diagramType === "V"
          ? isometric_3d.diagram.shearLabelXOffset
          : 0;
    const graphPoint = projectPoint(boardPoint(-label.horizontalValue, graphOffset));
    drawer.drawCircle(graphPoint, isometric_3d.diagram.pointRadius, { lineColor, thickness: 1, fill: lineColor });
    // tinted in the diagram's line colour, like the 2D side view, so M/V/N/D values stay apart
    drawer.drawText(
      projectPoint(boardPoint(-label.horizontalValue + xOffset, labelOffset)),
      label.text,
      { ...label_style, lineColor },
      { horiAlign: "left", vertAlign: "middle" },
    );
  }
};

const drawResultBoard = (context: IsometricDrawingContext, settings: ResultBoardSettings, currDir: GraphDirection) => {
  const { drawer, projectData, halfY, halfZ, columnHeight, point, projectPoint, outlineColor } = context;
  const {
    cardboardDistance,
    laneWidth,
    results,
    selectedLcombGuid,
    showCardboard,
    startAtOuterCenterLine,
    overlapClearance,
    headerOffsets,
  } = settings;
  const half = currDir === "xy" ? halfY : halfZ;
  const bottom = -0;
  const top = -columnHeight;
  const oppositeDir: GraphDirection = currDir === "xy" ? "xz" : "xy";
  const boardNear = Math.max(
    half + cardboardDistance,
    loadPanelOuterOffset(
      projectData,
      selectedLcombGuid,
      oppositeDir,
      cardboardDistance,
      halfY,
      halfZ,
      context.scaleYZ,
      startAtOuterCenterLine,
      settings.offsetLoadsFromColumn,
      settings.loadColumnSpacing,
    ) +
    overlapClearance / ISOMETRIC_HORIZONTAL_SCALE,
  );
  const boardFar = boardNear + resultBoardWidth(laneWidth);
  const boardPoint = (x: number, transverse: number): Point3D =>
    currDir === "xy"
      ? point(x, transverse, startAtOuterCenterLine ? 0 : -halfZ)
      : point(x, startAtOuterCenterLine ? 0 : -halfY, transverse);
  const elementResults = results.elementForces[selectedLcombGuid];
  const elementResultArray = elementResults ? Object.values(elementResults)[0] : null;
  const displacementResults = results.displacements[selectedLcombGuid];
  const displacementResultArray = displacementResults ? Object.values(displacementResults)[0] : null;
  // The 1st order counterpart (App's showBothOrders), only present when both order toggles are
  // on at once - mirrors the 2D side view's elementForcesSecondary/displacementsSecondary.
  const secondaryElementResults = results.elementForcesSecondary?.[selectedLcombGuid];
  const secondaryElementResultArray = secondaryElementResults ? Object.values(secondaryElementResults)[0] : null;
  const secondaryDisplacementResults = results.displacementsSecondary?.[selectedLcombGuid];
  const secondaryDisplacementResultArray = secondaryDisplacementResults
    ? Object.values(secondaryDisplacementResults)[0]
    : null;
  const diagramValues = graphTypes.map((type) => ({
    type,
    values: CreateGraphValues(elementResultArray ?? [], displacementResultArray ?? [], currDir, type, 1),
    secondaryValues: secondaryElementResultArray
      ? CreateGraphValues(secondaryElementResultArray, secondaryDisplacementResultArray ?? [], currDir, type, 1)
      : undefined,
  }));

  if (diagramValues.every(({ values }) => values.length === 0)) return;

  if (showCardboard) {
    const boardFrame = new PathBuilder()
      .moveTo(projectPoint(boardPoint(bottom, boardNear)))
      .drawLineTo(projectPoint(boardPoint(top, boardNear)))
      .drawLineTo(projectPoint(boardPoint(top, boardFar)))
      .drawLineTo(projectPoint(boardPoint(bottom, boardFar)))
      .closePath();
    drawer.drawPath(
      {
        lineColor: isometric_3d.colors.guide,
        thickness: isometric_3d.board.frameThickness,
        fill: null,
      },
      boardFrame,
    );
  }

  const boardColor = isometric_3d.colors.guide;
  for (let lane = 0; lane < RESULT_BOARD_LANE_COUNT; lane++) {
    const zeroOffset = boardNear + laneWidth * (lane + 0.5);
    drawer.drawLine(projectPoint(boardPoint(bottom, zeroOffset)), projectPoint(boardPoint(top, zeroOffset)), {
      lineColor: boardColor,
      thickness: isometric_3d.board.guideThickness,
      dashed: true,
    });
  }

  for (const { type, values, secondaryValues } of diagramValues) {
    if (values.length > 0) drawDiagram(context, settings, currDir, boardNear, boardPoint, type, values, secondaryValues);
  }

  const availableTypes = new Set(diagramValues.filter(({ values }) => values.length > 0).map(({ type }) => type));
  const headerX = top - isometric_3d.diagram.headerOffset;
  const headerFontSize = typeof diagram_header_style.textSize === "number" ? diagram_header_style.textSize : 12;
  // the same header layout as the 2D side view: one segment per diagram, each in its own colour
  const drawHeader = (lane: number, laneTypes: ResultBoardDiagramType[]) => {
    const types = laneTypes.filter((type) => availableTypes.has(type));
    if (types.length === 0) return;
    const segments = types.map((type) => ({ type, color: diagramColor(type) }));
    const laneOffset = headerOffsets[resultBoardScreenSide(currDir, context.rightHand)][lane];
    const anchor = projectPoint(boardPoint(headerX - laneOffset, boardNear + laneWidth * (lane + 0.5)));
    for (const run of layoutDiagramHeader(segments, currDir, headerFontSize)) {
      drawer.drawText(
        anchor,
        run.text,
        { ...diagram_header_style, lineColor: run.color, textSize: run.fontSize },
        { horiAlign: "left", vertAlign: "bottom", horiOffset: run.horiOffset, vertOffset: run.vertOffset },
      );
    }
  };

  drawHeader(0, ["N"]);
  drawHeader(1, ["M", "V"]);
  drawHeader(2, ["D"]);
};

const drawResultBoards = (context: IsometricDrawingContext, settings: ResultBoardSettings) => {
  for (const currDir of graphDirections) drawResultBoard(context, settings, currDir);
};

const drawMemberCenterLine = ({ drawer, columnHeight, point, projectPoint }: IsometricDrawingContext) => {
  drawer.drawLine(projectPoint(point(0, 0, 0)), projectPoint(point(-columnHeight, 0, 0)), {
    lineColor: isometric_3d.colors.guide,
    thickness: isometric_3d.board.guideThickness,
    dashed: true,
  });
};

const drawBucklingCurve = (
  { drawer, columnHeight, halfY, point, projectPoint }: IsometricDrawingContext,
  results: DiagramResults,
) => {
  const mode = results.bucklingMode?.[1] ?? [];
  const bucklingData = create_buckling_data(true, columnHeight, mode, false);
  if (bucklingData.length === 0) return;

  const maxValue = Math.max(...bucklingData.map((value) => Math.abs(value.value)), 1e-9);
  const path = new PathBuilder();
  bucklingData.forEach((value, index) => {
    const offset = halfY + isometric_3d.buckling.offset + (value.value / maxValue) * isometric_3d.buckling.amplitude;
    const projectedPoint = projectPoint(point(-value.at, offset, 0));
    if (index === 0) path.moveTo(projectedPoint);
    else path.drawLineTo(projectedPoint);
  });
  drawer.drawPath({ lineColor: isometric_3d.colors.outline, thickness: 1, fill: null, dashed: true }, path);
};

/**
 * Queues each support mesh's faces and outlines for a separate pass before the column is drawn.
 *
 * Visibility and shading read the mesh's *stored* normals, in the shared ng space (x up), scaled.
 * They are deliberately not evaluated in this view's drawing space: that space flips x to point
 * down, and a single-axis flip is a mirror, so a normal recovered from the winding of a face there
 * points inwards and the cull would keep the *inside* of every solid - see the note at the top of
 * mesh.ts. Depth is unaffected by the flip (dot((-x, y, z), (-1, 1, 1)) == dot((x, y, z), (1, 1, 1))),
 * so these depths are directly comparable with the column's.
 */
const drawSupportMeshes = (
  {
    drawer,
    painter,
    hiddenLines,
    scale,
    drawFaces,
    shadeFaces,
    lightDirection,
    debugExtrusion,
    projectPoint,
  }: IsometricDrawingContext,
  instances: MeshInstance[],
) => {
  for (const instance of instances) {
    const placed = transformMesh(instance.mesh, instance);
    const viewSpace = scalePlacedMesh(placed, { x: scale.x, y: scale.yz, z: scale.yz });
    const visible = visibleMeshFaces(viewSpace, instance.mesh, ISOMETRIC_VIEW_DIRECTION);
    // Only genuine shape features are outlined - a real corner (any edge touching a flat
    // primitive face) or an exact silhouette (a tessellation seam between two smooth/curved
    // facets whose visibility flips) - see classifyMeshEdges' docs.
    const classified = classifyMeshEdges(instance.mesh, viewSpace, ISOMETRIC_VIEW_DIRECTION);

    // drawing space has x pointing down; projectPoint applies the scale itself
    const drawingPoints: Point3D[] = placed.positions.map((p) => ({ x: -p.x, y: p.y, z: p.z }));

    for (const { face, normal, depth } of visible) {
      const facePoints = face.vertices.map((index) => drawingPoints[index]);
      hiddenLines.addOccluder([facePoints]);
      if (!drawFaces) continue;
      const color = shadeFaces ? shadeFaceColor(normal, lightDirection) : FACE_SIDE_COLOR;
      painter.addFace(depth, () => {
        const path = new PathBuilder().moveTo(projectPoint(facePoints[0]));
        facePoints.slice(1).forEach((p) => path.drawLineTo(projectPoint(p)));
        path.closePath();
        drawer.drawPath({ lineColor: color, thickness: FACE_SEAM_STROKE_PX, fill: color }, path);
      });
    }

    for (const edge of classified) {
      if (!edge.keep) continue;
      hiddenLines.addEdge(
        drawingPoints[edge.from],
        drawingPoints[edge.to],
        debugExtrusion ? edge.debugInfo : undefined,
      );
    }
  }
};

const drawLoads = (
  { drawer, projectData, columnHeight, halfY, halfZ, scaleYZ, point, projectPoint }: IsometricDrawingContext,
  {
    selectedLcombGuid,
    cardboardDistance,
    showDimensions,
    startAtOuterCenterLine,
    showOnOppositeSide,
    showCardboard,
    offsetLoadsFromColumn,
    loadColumnSpacing,
  }: LoadDrawingSettings,
) => {
  if (!projectData) return;

  const { vertical, horizontal, line } = get_loads(projectData);
  const selectedLoadcaseGuids = getSelectedLoadcaseGuids(projectData, selectedLcombGuid);
  const isSelected = (load: ng.load.Data) => (load.lcaseGuid ? selectedLoadcaseGuids.has(load.lcaseGuid) : false);
  const selectedHorizontal = horizontal.filter((load) => isSelected(load) && load.direction);
  const selectedLine = line.filter(
    (load) => isSelected(load) && load.direction && classify_direction(load.direction) !== "x",
  );
  const selectedAxialLine = line.filter(
    (load) => isSelected(load) && load.direction && classify_direction(load.direction) === "x",
  );
  const horizontalMagnitudes = [
    ...selectedHorizontal.map(force_from_point_load),
    ...selectedLine.flatMap((load) => load.values ?? []),
  ]
    .map(Math.abs)
    .filter(Number.isFinite);
  const horizontalScale =
    horizontalMagnitudes.length > 0
      ? (isometric_3d.load.horizontalAmplitude - isometric_3d.load.minimumAmplitude) /
      Math.max(...horizontalMagnitudes, 1e-9)
      : 0;
  const selectedVertical = vertical.filter(isSelected);
  const verticalMagnitudes = [
    ...selectedVertical.map(force_from_point_load),
    ...selectedAxialLine.flatMap((load) => load.values ?? []),
  ]
    .map(Math.abs)
    .filter(Number.isFinite);
  const verticalScale =
    verticalMagnitudes.length > 0
      ? (isometric_3d.load.verticalAmplitude - isometric_3d.load.minimumAmplitude) /
      Math.max(...verticalMagnitudes, 1e-9)
      : 0;
  const boardPoint = (currDir: GraphDirection, x: number, transverse: number): Point3D =>
    currDir === "xy"
      ? point(x, transverse, startAtOuterCenterLine ? 0 : -halfZ)
      : point(x, startAtOuterCenterLine ? 0 : -halfY, transverse);
  const transverseLoads = [...selectedHorizontal, ...selectedLine];
  const boardNearFor = (currDir: GraphDirection) =>
    loadBoardNear(
      currDir,
      halfY,
      halfZ,
      scaleYZ,
      cardboardDistance,
      startAtOuterCenterLine,
      offsetLoadsFromColumn,
      loadColumnSpacing,
    );
  const lanesFor = (currDir: GraphDirection) =>
    loadCaseLanes(
      transverseLoads.filter((candidate) => {
        if (!candidate.direction) return false;
        const direction = classify_direction(candidate.direction);
        return (direction === "y" ? "xy" : direction === "z" ? "xz" : null) === currDir;
      }),
      (load) =>
        isometric_3d.load.minimumAmplitude +
        (selectedLine.includes(load)
          ? Math.max(...(load.values ?? []).map(Math.abs), 0)
          : Math.abs(force_from_point_load(load))) * horizontalScale,
      isometric_3d.load.laneSpacing,
    );
  const lanes = { xy: lanesFor("xy"), xz: lanesFor("xz") };
  const loadBase = (currDir: GraphDirection, load: ng.load.Data) => {
    const side = showOnOppositeSide ? -1 : 1;
    return side * (boardNearFor(currDir) + lanes[currDir].offsetOf(load));
  };
  const loadFar = (base: number, value: number) =>
    base +
    (showOnOppositeSide ? -1 : 1) * (isometric_3d.load.minimumAmplitude + Math.abs(value) * horizontalScale);
  const loadLabelOffset = showOnOppositeSide ? -isometric_3d.load.labelOffset : isometric_3d.load.labelOffset;
  if (showCardboard && showOnOppositeSide) {
    for (const currDir of graphDirections) {
      const loads = transverseLoads.filter((load) => {
        if (!load.direction) return false;
        const direction = classify_direction(load.direction);
        return (direction === "y" ? "xy" : direction === "z" ? "xz" : null) === currDir;
      });
      if (loads.length === 0) continue;

      const boardNear = boardNearFor(currDir);
      const boardFar = Math.max(
        boardNear,
        ...loads.map((load) => {
          const value = selectedLine.includes(load)
            ? Math.max(...(load.values ?? []).map(Math.abs), 0)
            : Math.abs(force_from_point_load(load));
          return Math.abs(loadFar(loadBase(currDir, load), value)) + isometric_3d.board.loadPadding;
        }),
      );
      const boardPoint = (x: number, transverse: number) =>
        currDir === "xy"
          ? point(x, transverse, startAtOuterCenterLine ? 0 : -halfZ)
          : point(x, startAtOuterCenterLine ? 0 : -halfY, transverse);
      const loadBoardFrame = new PathBuilder()
        .moveTo(projectPoint(boardPoint(0, -boardNear)))
        .drawLineTo(projectPoint(boardPoint(-columnHeight, -boardNear)))
        .drawLineTo(projectPoint(boardPoint(-columnHeight, -boardFar)))
        .drawLineTo(projectPoint(boardPoint(0, -boardFar)))
        .closePath();
      drawer.drawPath(
        {
          lineColor: isometric_3d.colors.guide,
          thickness: isometric_3d.board.frameThickness,
          fill: null,
        },
        loadBoardFrame,
      );
    }
  }

  for (const load of selectedHorizontal) {
    if (!load.direction) continue;
    const direction = classify_direction(load.direction);
    if (direction === "x") continue;
    const currDir: GraphDirection = direction === "y" ? "xy" : "xz";
    const position = load.positions?.[0];
    const x = -(position?.x ?? 0);
    const color = loadcases_getColor(projectData, load.lcaseGuid).toHex();
    const force = force_from_point_load(load);
    if (!Number.isFinite(force) || Math.abs(force) < 1e-9) continue;

    const base = loadBase(currDir, load);
    const far = loadFar(base, force);
    const fromPoint = boardPoint(currDir, x, force < 0 ? base : far);
    const toPoint = boardPoint(currDir, x, force < 0 ? far : base);
    drawer.drawLine(
      projectPoint(fromPoint),
      projectPoint(toPoint),
      { lineColor: color, thickness: 1.2 },
      { endArrow: "load-arrow" },
    );
    if (showDimensions) {
      drawer.drawText(
        projectPoint(boardPoint(currDir, x, far + loadLabelOffset)),
        `${Math.abs(force) / 1000} kN`,
        { ...cs_force_text, lineColor: color },
        { horiAlign: "center", vertAlign: "bottom" },
      );
    }
  }

  for (const load of selectedLine) {
    if (!load.direction || load.positions.length < 2 || load.values.length < 2) continue;
    const direction = classify_direction(load.direction);
    const currDir: GraphDirection = direction === "y" ? "xy" : "xz";
    const start = load.positions[0].x;
    const end = load.positions[1].x;
    const startValue = load.values[0];
    const endValue = load.values[1];
    const color = loadcases_getColor(projectData, load.lcaseGuid).toHex();
    const base = loadBase(currDir, load);
    const stepCount = Math.max(1, Math.round(Math.abs(end - start) / isometric_3d.load.distributedHorizontalStep));
    const envelope = new PathBuilder();
    const zeroFraction = startValue * endValue < 0 ? startValue / (startValue - endValue) : null;

    for (let step = 0; step <= stepCount; step++) {
      const fraction = step / stepCount;
      const x = -(start + (end - start) * fraction);
      const value = startValue + (endValue - startValue) * fraction;
      const far = Math.abs(value) < 1e-9 ? base : loadFar(base, value);
      if (Math.abs(value) >= 1e-9) {
        const fromPoint = boardPoint(currDir, x, value < 0 ? base : far);
        const toPoint = boardPoint(currDir, x, value < 0 ? far : base);
        drawer.drawLine(
          projectPoint(fromPoint),
          projectPoint(toPoint),
          { lineColor: color, thickness: 1 },
          { endArrow: "load-arrow" },
        );
      }
      const envelopePoint = projectPoint(boardPoint(currDir, x, far));
      if (step === 0) envelope.moveTo(envelopePoint);
      else {
        if (zeroFraction !== null && (step - 1) / stepCount < zeroFraction && zeroFraction < fraction) {
          const zeroX = -(start + (end - start) * zeroFraction);
          envelope.drawLineTo(projectPoint(boardPoint(currDir, zeroX, base)));
        }
        envelope.drawLineTo(envelopePoint);
      }
    }
    drawer.drawPath({ lineColor: color, thickness: 1, fill: null }, envelope);
    drawer.drawLine(projectPoint(boardPoint(currDir, -start, base)), projectPoint(boardPoint(currDir, -end, base)), {
      lineColor: color,
      thickness: 1,
    });
    if (showDimensions) {
      const drawLineLoadLabel = (x: number, value: number) => {
        const far = Math.abs(value) < 1e-9 ? base : loadFar(base, value);
        drawer.drawText(
          projectPoint(boardPoint(currDir, -x, far + loadLabelOffset)),
          `${Math.abs(value) / 1000} kN/m`,
          { ...cs_force_text, lineColor: color },
          { horiAlign: "center", vertAlign: "bottom" },
        );
      };
      drawLineLoadLabel(start, startValue);
      if (Math.abs(startValue - endValue) >= 1) drawLineLoadLabel(end, endValue);
    }
  }

  const drawAxialArrow = (
    x: number,
    value: number,
    length: number,
    color: string,
    thickness: number,
    y = 0,
    z = 0,
  ) => {
    const fromX = value < 0 ? x : x - length;
    const toX = value < 0 ? x - length : x;
    drawer.drawLine(
      projectPoint(point(fromX, y, z)),
      projectPoint(point(toX, y, z)),
      { lineColor: color, thickness },
      { endArrow: "load-arrow" },
    );
  };

  for (const load of selectedAxialLine) {
    if (load.positions.length < 2 || load.values.length < 2) continue;
    const start = load.positions[0].x;
    const end = load.positions[1].x;
    const startValue = load.values[0];
    const endValue = load.values[1];
    const color = loadcases_getColor(projectData, load.lcaseGuid).toHex();
    const stepCount = Math.max(1, Math.round(Math.abs(end - start) / isometric_3d.load.distributedVerticalStep));

    for (let step = 1; step <= stepCount; step++) {
      const fraction = step / stepCount;
      const x = -(start + (end - start) * fraction);
      const value = startValue + (endValue - startValue) * fraction;
      if (Math.abs(value) < 1e-9) continue;
      const length = isometric_3d.load.minimumAmplitude + Math.abs(value) * verticalScale;
      drawAxialArrow(x, value, length, color, 1);
    }
  }

  // skip repeats: loads at the same point would overdraw the lines, making them look darker/heavier
  const drawnEccentricities = new Set<string>();
  for (const load of selectedVertical) {
    const position = load.positions?.[0];
    const force = force_from_point_load(load);
    if (!Number.isFinite(force) || Math.abs(force) < 1e-9) continue;
    const x = -(position?.x ?? 0);
    const path = eccentricityOffsetPath(position?.y ?? 0, position?.z ?? 0);
    const color = loadcases_getColor(projectData, load.lcaseGuid).toHex();
    const key = `${x}|${position?.y ?? 0}|${position?.z ?? 0}|${color}`;
    if (path.length < 2 || drawnEccentricities.has(key)) continue;
    drawnEccentricities.add(key);
    for (let i = 1; i < path.length; i++) {
      const from = projectPoint(point(x, path[i - 1].y, path[i - 1].z));
      const to = projectPoint(point(x, path[i].y, path[i].z));
      drawer.drawLine(from, to, {
        lineColor: color,
        thickness: isometric_3d.load.eccentricityThickness,
        dashed: true,
      });
    }
  }

  for (const load of selectedVertical) {
    const position = load.positions?.[0];
    const x = -(position?.x ?? 0);
    const color = loadcases_getColor(projectData, load.lcaseGuid).toHex();
    const force = force_from_point_load(load);
    if (!Number.isFinite(force) || Math.abs(force) < 1e-9) continue;

    const length = isometric_3d.load.minimumAmplitude + Math.abs(force) * verticalScale;
    const fromX = force < 0 ? x : x - length;
    const toX = force < 0 ? x - length : x;
    const y = position?.y ?? 0;
    const z = position?.z ?? 0;
    drawAxialArrow(x, force, length, color, 1.2, y, z);
    if (showDimensions) {
      drawer.drawText(
        projectPoint(point((fromX + toX) / 2, y, z)),
        `${Math.abs(force) / 1000} kN`,
        { ...cs_force_text, lineColor: color },
        { horiAlign: "center", vertAlign: "middle" },
      );
    }
  }
};

const drawAxisTriad = (
  { drawer, scale, halfY, halfZ, outlineColor, point, projectPoint, rightHand }: IsometricDrawingContext,
  { size, offset, labelOffset, lightRay: light }: AxisSettings,
) => {
  const axisOrigin = point(-0, halfY + offset, halfZ + offset);
  const axisXEnd = point(axisOrigin.x - size / scale.x, axisOrigin.y, axisOrigin.z);
  const axisYEnd = point(axisOrigin.x, axisOrigin.y + size / scale.yz, axisOrigin.z);
  const axisZEnd = point(axisOrigin.x, axisOrigin.y, axisOrigin.z + size / scale.yz);
  const drawAxis = (end: Point3D, color: string, dashed = false) =>
    drawer.drawLine(
      projectPoint(axisOrigin),
      projectPoint(end),
      { lineColor: color, thickness: isometric_3d.axis.lineThickness, dashed },
      { endArrow: "axis-arrow" },
    );

  drawAxis(axisXEnd, isometric_3d.axis.xColor);
  drawAxis(axisYEnd, isometric_3d.axis.yColor);
  drawAxis(axisZEnd, isometric_3d.axis.zColor);
  if (light) {
    const lightEnd = point(
      axisOrigin.x - (light.x * size) / scale.x,
      axisOrigin.y + (light.y * size) / scale.yz,
      axisOrigin.z + (light.z * size) / scale.yz,
    );
    drawer.drawLine(projectPoint(lightEnd), projectPoint(point(axisOrigin.x, lightEnd.y, lightEnd.z)), {
      lineColor: isometric_3d.axis.lightProjectionColor,
      thickness: 1,
      dashed: true,
    });
    drawAxis(lightEnd, isometric_3d.axis.lightColor, light.x < 0);
  }
  drawer.drawCircle(projectPoint(axisOrigin), isometric_3d.axis.originRadius, {
    lineColor: outlineColor,
    thickness: 1,
    fill: outlineColor,
  });
  drawer.drawText(
    projectPoint(axisXEnd),
    "X",
    { lineColor: outlineColor, textSize: isometric_3d.axis.labelTextSize },
    { horiAlign: "center", vertAlign: "bottom", horiOffset: labelOffset, vertOffset: -3 },
  );
  drawer.drawText(
    projectPoint(axisYEnd),
    "Y",
    { lineColor: outlineColor, textSize: isometric_3d.axis.labelTextSize },
    {
      horiAlign: rightHand ? "right" : "left",
      vertAlign: "top",
      horiOffset: rightHand ? -4 : 4,
      vertOffset: 2 + labelOffset,
    },
  );
  drawer.drawText(
    projectPoint(axisZEnd),
    "Z",
    { lineColor: outlineColor, textSize: isometric_3d.axis.labelTextSize },
    {
      horiAlign: rightHand ? "left" : "right",
      vertAlign: "top",
      horiOffset: rightHand ? 4 : -4,
      vertOffset: 2 + labelOffset,
    },
  );
};

export const SvgIsometricColumnView = ({ results, selectedLcombGuid, graphPointsMode }: View3DProps) => {
  const { project: projectSource } = useProject();
  const isDev = useDevConfig();
  const autoZoom = useAutoZoomViewport();
  const scaleX = useSelector(selectScale3dX);
  const scaleYZ = useSelector(selectScale3dYZ);
  const cardboardDistance = useSelector(selectCardboardDistance3d);
  const resultBoardLaneWidth = useSelector(selectResultBoardLaneWidth3d);
  const resultBoardGraphAmplitude = useSelector(selectResultBoardGraphAmplitude3d);
  const resultBoardClearance = useSelector(selectResultBoardClearance3d);
  const resultBoardHeaderOffsetsLeft = useSelector(selectResultBoardHeaderOffsetsLeft3d);
  const resultBoardHeaderOffsetsRight = useSelector(selectResultBoardHeaderOffsetsRight3d);
  const offsetLoadsFromColumn = useSelector(selectOffsetLoadsFromColumn3d);
  const axisSize = useSelector(selectAxisSize3d);
  const axisOffset = useSelector(selectAxisOffset3d);
  const axisLabelOffset = useSelector(selectAxisLabelOffset3d);
  const rightHand = useSelector(selectRightHand3d);
  const startCardboardsAtOuterCenterLine = useSelector(selectStartCardboardsAtOuterCenterLine3d);
  const show3dLoadsOnOppositeSide = useSelector(selectShow3dLoadsOnOppositeSide);
  const showDimensions = useSelector(selectShowSectionDimensions);
  const showCardboard = useSelector(selectShow3dCardboard);
  const showReinforcement = useSelector(selectShow3dReinforcement);
  const showBuckling = useSelector(selectShowSectionBuckling);
  const loadColumnSpacing = useSelector(selectLoadColumnSpacing3d);
  const geometryDebug = useSelector(selectGeometryDebug3d);
  const debugExtrusion = useSelector(selectDebugExtrusion3d);
  const debugExtrusionNormals = useSelector(selectDebugExtrusionNormals3d);
  const debugExtrusionNormalsCenter = useSelector(selectDebugExtrusionNormalsCenter3d);
  const debugExtrusionNormalsRadius = useSelector(selectDebugExtrusionNormalsRadius3d);
  const drawFaces = useSelector(selectDrawFaces3d);
  const shadeFaces = useSelector(selectShadeFaces3d);
  const lightYaw = useSelector(selectLightYaw3d);
  const lightPitch = useSelector(selectLightPitch3d);
  const showLightDirection = useSelector(selectShowLightDirection3d);
  const hiddenLines = useSelector(selectHiddenLines3d);
  const hiddenLineGrey = useSelector(selectHiddenLineGrey3d);
  const shadeLighterTimberPercent = useSelector(selectShadeLighterTimberPercent3d);
  const shadeLighterPercent = useSelector(selectShadeLighterPercent3d);
  const shadeDarkerPercent = useSelector(selectShadeDarkerPercent3d);
  const scale: IsoScale = { x: scaleX, yz: scaleYZ };
  const project = {
    ...projectSource,
    data: ng.frontend.column.Project.fromObject(projectSource?.data),
  };
  const projectData = project.data;
  const columnSideColor = columnMaterialFill(projectData);
  const lighterPercent = columnLighterPercent(projectData, shadeLighterTimberPercent, shadeLighterPercent);
  const columnTopColor = columnTopFill(columnSideColor, lighterPercent);
  const fakeSilhouette = shouldUseFakeSilhouetteForSection(projectData);
  const geomExtent = projectData ? calculateGeomCrossSection(projectData) : null;
  const halfY = halfExtentFor(projectData, geomExtent, "xy");
  const halfZ = halfExtentFor(projectData, geomExtent, "xz");
  const { height: columnHeight } = calculate_column_side_geom_size(projectData, "xy");
  const outlineColor = isometric_3d.colors.outline;
  const drawingContext: IsometricDrawingContext = {
    drawer: new Drawer<ArrowId>(),
    projectData,
    scale,
    rightHand,
    scaleYZ,
    columnHeight,
    halfY,
    halfZ,
    outlineColor,
    fakeSilhouette,
    debugExtrusion,
    debugExtrusionNormals,
    debugExtrusionNormalsCenter,
    debugExtrusionNormalsRadius,
    drawFaces,
    shadeFaces,
    lightDirection: ngLightDirection(lightYaw, lightPitch),
    hiddenLineStyle: hiddenLineStyleFor(hiddenLines, outlineColor, hiddenLineGrey),
    columnSideColor,
    columnTopColor,
    columnShadedColor: (lightness) =>
      columnShadedFill(columnSideColor, lightness, lighterPercent, shadeDarkerPercent),
    painter: new PainterQueue(),
    hiddenLines: new HiddenLineScene((p) => ({
      point: isoProject(p, scale, rightHand),
      depth: dot({ x: p.x * scale.x, y: p.y * scale.yz, z: p.z * scale.yz }, SVG_ISOMETRIC_VIEW_DIRECTION),
    })),
    point: (x, y, z) => ({ x, y, z }),
    projectPoint: (point) => isoProject(point, scale, rightHand),
  };

  const supportInstances = geometryDebug ? [] : collectSupportMeshInstances(projectData, { halfY, halfZ });

  // Supports go behind the column, then its side faces, then its top face. Edges are drawn last,
  // split into visible and hidden pieces against every front face.
  drawSupportMeshes(drawingContext, supportInstances);
  drawingContext.painter.flush();
  const crossSection = queueColumnSides(drawingContext);
  drawingContext.painter.flush();
  drawColumnTopFace(drawingContext, crossSection);
  drawEdges(drawingContext);

  if (showReinforcement) drawLongitudinalReinforcement(drawingContext);
  if (!geometryDebug) {
    drawMemberCenterLine(drawingContext);
    const sharedValueRanges = isometricDiagramValueRanges(results, graphPointsMode, projectData?.data?.output);
    drawResultBoards(drawingContext, {
      results,
      selectedLcombGuid,
      graphPointsMode,
      cardboardDistance,
      laneWidth: resultBoardLaneWidth,
      graphAmplitude: resultBoardGraphAmplitude,
      overlapClearance: resultBoardClearance,
      headerOffsets: { left: resultBoardHeaderOffsetsLeft, right: resultBoardHeaderOffsetsRight },
      offsetLoadsFromColumn,
      loadColumnSpacing,
      showCardboard,
      startAtOuterCenterLine: startCardboardsAtOuterCenterLine,
      sharedValueRanges,
    });
    if (showBuckling) drawBucklingCurve(drawingContext, results);
    drawLoads(drawingContext, {
      selectedLcombGuid,
      cardboardDistance,
      showDimensions,
      startAtOuterCenterLine: startCardboardsAtOuterCenterLine,
      showOnOppositeSide: show3dLoadsOnOppositeSide,
      showCardboard,
      offsetLoadsFromColumn,
      loadColumnSpacing,
    });
  }
  if (!geometryDebug) {
    drawAxisTriad(drawingContext, {
      size: axisSize,
      offset: axisOffset,
      labelOffset: axisLabelOffset,
      lightRay: showLightDirection
        ? {
          x: -drawingContext.lightDirection.x,
          y: -drawingContext.lightDirection.y,
          z: -drawingContext.lightDirection.z,
        }
        : null,
    });
  }

  return (
    <Svg<ArrowId>
      items={drawingContext.drawer.toDrawables()}
      markers={ColumnMarkers()}
      isDev={isDev}
      autoZoom={autoZoom}
    />
  );
};
